import { API_URL } from './api';
import { getToken, getAuthHeader } from '@/state/authState';
export interface StreamingRequestOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown>;
  skipAuth?: boolean;
}
export interface StreamingResponse {
  response: Response;
  reader: ReadableStreamDefaultReader<Uint8Array>;
  abort: () => void;
}
export async function streamingFetch(
  endpoint: string,
  options: StreamingRequestOptions = {}
): Promise<StreamingResponse> {
  const { body, skipAuth = false, ...fetchOptions } = options;
  const abortController = new AbortController();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (!skipAuth) {
    const authHeader = getAuthHeader();
    if (authHeader) {
      (headers as Record<string, string>)['Authorization'] = authHeader;
    }
  }
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal: abortController.signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new StreamingApiError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData
    );
    throw error;
  }
  const reader = response.body?.getReader();
  if (!reader) {
    throw new StreamingApiError('No response body reader available', 0);
  }
  return {
    response,
    reader,
    abort: () => abortController.abort(),
  };
}
export class StreamingApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data?: unknown
  ) {
    super(message);
    this.name = 'StreamingApiError';
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}
export async function* parseSSEStream<T = unknown>(
  reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<{
  event: string;
  data: T;
}> {
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      let currentEvent: string | null = null;
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7);
        } else if (line.startsWith('data: ') && currentEvent) {
          const data = line.slice(6);
          try {
            yield { event: currentEvent, data: JSON.parse(data) as T };
          } catch {
            console.warn('Failed to parse SSE data:', data);
          }
          currentEvent = null;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
